import {Storage as Store} from '@capacitor/storage';
import {clear, get, set} from 'idb-keyval';
import store from "@/store/index";
import data from "bootstrap/js/src/dom/data";
import axios from "axios";
import {responses} from "@/utils/responses";
import router from "@/router";
import {nextTick} from 'vue'
import {openDB} from 'idb';

export const ZRA_DETAILS = 'zra_details'
export const DEFAULT_STORE = 'default_store'
export const ALL_STORES = 'all_stores'
export const FRANCHISE_SETTINGS = 'franchise_settings'
export const REGISTRATION_DETAILS = 'registration_details'

/**
 * Opaque deeplink session issued by {@code POST /posAuth/{DB}/login}.
 * Persisted at login time and read by the deep-link sender
 * ({@code openOnlineBackOffice}) to build the URL. The receiving
 * app (Lytning Core / Online BackOffice Front) calls
 * {@code GET /posAuth/{DB}/session/{sessionId}} to hydrate.
 *
 * <p>Keys are kept short and non-PII so an audit of the IndexedDB
 * store can't be confused with credentials.
 */
export const CURRENT_SESSION = 'current_session'

export const ADMIN = 'admin'
export const CATEGORIES = 'categories'
export const AllUsers = 'all_users'
export const AllSecurities = 'securities'
export const CurrentUser = 'current_user'
export const STOCK = 'stock'
export const STOCK_IMAGES = 'stockImages'
export const PAYMENT_METHODS = 'payment_methods'
export const STOCK_SETTING = 'stock_setting'
export const TAX_LABELS = 'taxLabels'

export const HAS543 = 'has543'
export const HAS_LOYALTY = 'hasLoyalty'
export const HASKAZANG = 'hasKazang'
export const KAZANGSESSION = 'kazangSession'
export const KAZANGPRODUCTS = 'kazangProductList'
export const STORE_CURRENCIES = 'storeCurrencies'

export const FLOAT = 'float'
export const DEVICE_SETTINGS = 'device_settings'
export const SAVED_TABLES = 'saved_tables'

export const BASE_CURRENCY = 'base_currency'
export const ZED_MOBILE_STATUS = 'zedMobileStatus'

export const DISCOUNT_SPECIALS = 'discountSpecials'
export const MULTI_SPECIALS = 'multiSpecials'
export const COMBO_SPECIALS = 'comboSpecials'
export const SYSTEM_SETTINGS = 'systemSettings'

export const SUPERVISOR = 'supervisor'

const notEmpty = (val) => {
    if (!val) return false
    return val !== ''
}
const encryptAdmin = (val) => {
    return window.btoa(val);
}
const decryptAdmin = (val) => {
    if (!val) return undefined;
    if (val.toString().charAt(0) === `"`) {
        val = val.toString().slice(1, -1);
    }
    return JSON.parse(window.atob(val))
}

// Database configuration
const DB_NAME = 'pos-db';
const DB_VERSION = 3;
const STORES = {
    MAIN: 'main',
    ADMIN: 'adminUsers',
    SESSIONS: 'sessions',
    DEFAULT_STORE
};

// Initialize IndexedDB
async function getDB() {
    return openDB(DB_NAME, DB_VERSION, {
        upgrade(db, oldVersion) {
            // Create object stores incrementally
            if (!db.objectStoreNames.contains(STORES.MAIN)) {
                db.createObjectStore(STORES.MAIN);
            }
            if (!db.objectStoreNames.contains(STORES.ADMIN)) {
                db.createObjectStore(STORES.ADMIN);
            }
            if (oldVersion < 3 && !db.objectStoreNames.contains(STORES.SESSIONS)) {
                db.createObjectStore(STORES.SESSIONS);
            }
        }
    });
}

// Updated storage methods
const getValue = async (key, storeName = STORES.MAIN) => {
    const db = await getDB();
    return db.transaction(storeName).store.get(key);
};

const setValue = async (key, value, storeName = STORES.MAIN) => {
    const db = await getDB();
    const tx = db.transaction(storeName, 'readwrite');
    await tx.store.put(value, key);
    await tx.done;
};

function isImageURL(url) {
    return new Promise(function (resolve, reject) {
        let img = new Image();
        img.onload = function () {
            resolve(true);
        };
        img.onerror = function () {
            resolve(false);
        };
        img.src = url;
    });
}

export const Storage = {
    logOutofStore: async () => {
        await Storage.setAdminUser(null)
        await Storage.setAllStores(null)
        await Storage.setAllUsers(null)
        await Storage.setCurrentUser(null)
        await Storage.setRegistrationDetails(null)
   
    },
    getCurrentStoreDB: async () => {
        if (!store.getters.isHydrated) {
            await new Promise((resolve) => {
                const interval = setInterval(() => {
                    if (store.getters.isHydrated) {
                        clearInterval(interval);
                        resolve();
                    }
                }, 50);  // Check every 50ms
            });
        }
        let defaultStore = store.getters.getDefaultStore;
        if (defaultStore) {
            if (!defaultStore) {
                axios.get(reportTomcatApi + `getFranchiseByDB?DB=${defaultStore.StoreDB}`)
                    .then(({data}) => store.commit(`setAdmin`, data))
            }
            return defaultStore.StoreDB;
        }
        if (!window.androidMode) {
            let storeDB;
            try {
                let response = await axios.get(hardwareApi + `utils/getStoreDB`);
                if (response.status === 404) {
                    responses.showWarning(`Request for hardware service update`)
                    return null;
                }
                storeDB = response.data;
                if (storeDB && storeDB !== 'none') {
                    console.trace(`Called`)
                    return axios.get(reportTomcatApi + `getStoresByDB?DB=${storeDB}`)
                        .then(async ({data}) => {
                            store.commit(`setRegisteredStores`, data);
                            let currentStore = data.find(s => s.StoreDB === storeDB);
                            if (currentStore) {
                                await Storage.setDefaultStore(currentStore);
                                axios.get(reportTomcatApi + `getFranchiseByDB?DB=${storeDB}`)
                                    .then(({data}) => store.commit(`setAdmin`, data))
                                if (router.currentRoute.name === `login` || router.currentRoute.name === `poslogin` || router.currentRoute.name === `setup`) {
                                    router.push({name: `poslogin`})
                                }
                                return currentStore.StoreDB;
                            }
                        })
                }
            } catch (e) {
            }
        }
        return null;
    },
    setAdminUser: async (user) => {
        const encrypted = encryptAdmin(JSON.stringify(user));
        await setValue(ADMIN, encrypted, STORES.ADMIN);
        store.commit('setAdmin', user);
    },
    getAdminUser: async () => {
        await Storage.waitForHydrate();
        let value = store.getters.getAdmin;
        if (value) return value;

        try {
            // Check IndexedDB first
            value = await getValue(ADMIN, STORES.ADMIN);
            if (value) return decryptAdmin(value);
        } catch (error) {
            console.error('IndexedDB access error:', error);
        }
        return null;
    },

    setAllStores: async (payload) => {
        // store/index.js state: registeredStores
        await setValue(ALL_STORES, payload);
        store.commit('setRegisteredStores', payload);
    },

    getAllStores: async () => {
        let value = store.getters.getRegisteredStores;
        if (value) return value;
        value = await getValue(ALL_STORES);
        return value;
    },

    setFranchiseSettings: async (payload) => {
        await setValue(FRANCHISE_SETTINGS, payload);
        store.commit('setFranchiseSettings', payload);
    },

    setRegistrationDetails: async (payload) => {
        await setValue(REGISTRATION_DETAILS, payload);
        store.commit('setRegistrationDetails', payload);
    },

    getFranchiseSettings: async () => {
        let value = store.getters.getFranchiseSettings;
        if (value) return value;
        value = await getValue(FRANCHISE_SETTINGS);
        return value;
    },

    getRegistrationDetails: async () => {
        let value = store.getters.getRegistrationDetails;
        if (value) return value;
        value = await getValue(REGISTRATION_DETAILS);
        return value;
    },

    setZRADetails: async (payload) => {
        await setValue(ZRA_DETAILS, payload);
    },

    setCategories: async (categories) => {
        await setValue(CATEGORIES, categories);
        store.commit('setCategories', categories);
    },

    getCategories: async () => {
        let value = store.getters.getCategories;
        if (value) return value;
        value = await getValue(CATEGORIES);
        return value;
    },

    setHas543: async (has543) => {
        await setValue(HAS543, has543);
        store.commit('setHas543', has543);
    },
    getHas543: async () => {
        return getValue(HAS543);
    },

    setStockImages: async (payload) => {
        let images = [];
        for (let i = 0; i < payload.length; i++) {
            let itemImage = payload[i];
            let isValid = await isImageURL(itemImage.Image)
            if (isValid) {
                images.push(itemImage)
            }
        }
        await setValue(STOCK_IMAGES, images);
        store.commit('setStockImages', images);
    },


    setHasKazang: async (payload) => {
        await setValue(HASKAZANG, payload);
        store.commit('setHasKazang', payload);
    },

    getHasKazang: async () => {
        const value = await getValue(HASKAZANG);
        return JSON.parse(value);
    },

    getZRADetails: async () => {
        const value = await getValue(ZRA_DETAILS);
        return JSON.parse(value);
    },
    setDefaultStore: async (defaultStore) => {
        await setValue(DEFAULT_STORE, defaultStore);
        store.commit('setDefaultStore', defaultStore);
    },
    getDefaultStore: async () => {
        if (!store.getters.isHydrated) {
            await new Promise((resolve) => {
                const interval = setInterval(() => {
                    if (store.getters.isHydrated) {
                        clearInterval(interval);
                        resolve();
                    }
                }, 50);  // Check every 50ms
            });
        }
        let value = store.getters.getDefaultStore;
        if (value) {
            if (!value) {
                axios.get(reportTomcatApi + `getFranchiseByDB?DB=${value.StoreDB}`)
                    .then(({data}) => store.commit(`setAdmin`, data))
            }
            setTimeout(() => {
                let name = router.currentRoute.name;
                if (name === 'login' || name === 'poslogin' || name === 'setup') {
                    router.push({name: 'poslogin'}).catch((err) => {
                        if (err.name !== 'NavigationDuplicated') {
                            console.error(err)
                        }
                    })
                }
            }, 0)
            return value;
        }
        value = await getValue(DEFAULT_STORE);
        if (value) {
            if (value) {
                axios.get(reportTomcatApi + `getFranchiseByDB?DB=${value.StoreDB}`)
                    .then(({data}) => store.commit(`setAdmin`, data))
            }
            setTimeout(() => {
                let name = router.currentRoute.name;
                if (name === 'login' || name === 'setup') {
                    router.push({name: 'poslogin'}).catch((err) => {
                        if (err.name !== 'NavigationDuplicated') {
                            console.error(err)
                        }
                    })
                }
            }, 0)
            return value;
        }
        if (!window.androidMode) {
            let storeDB;
            try {
                let response = await axios.get(hardwareApi + `utils/getStoreDB`);
                if (response.status === 404) {
                    responses.showWarning(`Request for hardware service update`)
                    return null;
                }
                storeDB = response.data;
                if (storeDB && storeDB !== 'none') {
                    console.trace(`Called`)
                    return axios.get(reportTomcatApi + `getStoresByDB?DB=${storeDB}`)
                        .then(async ({data}) => {
                            store.commit(`setRegisteredStores`, data);
                            let currentStore = data.find(s => s.StoreDB === storeDB);
                            if (currentStore) {
                                await Storage.setDefaultStore(currentStore);;
                                axios.get(reportTomcatApi + `getFranchiseByDB?DB=${storeDB}`)
                                    .then(({data}) => store.commit(`setAdmin`, data))
                                setTimeout(() => {
                                    let name = router.currentRoute.name;
                                    if (name === 'login' || name === 'setup') {
                                        router.push({name: 'poslogin'}).catch((err) => {
                                            if (err.name !== 'NavigationDuplicated') {
                                                console.error(err)
                                            }
                                        })
                                    }
                                }, 0)

                                return currentStore;
                            }
                        })
                }
            } catch (e) {
            }
        }
        return null;
    },

    getCurrentStoreID: async () => {
        let value = store.getters.getFranchiseSettings;
        if (value) return value.StoreId;
        value = await getValue(FRANCHISE_SETTINGS);
        if (!value) return null;
        return value.StoreId;
    },

    setAllUsers: async (users) => {
        // store/module/userModule.js state: users
        await setValue(AllUsers, users);
        store.commit('setAllUsers', users);
    },
    getAllUsers: async () => {
        let value = store.getters.getUsers;
        if (value) return value;
        value = await getValue(AllUsers);
        return value;
    },
    setAllSecurities: async (users) => {
        await setValue(AllSecurities, users);
        store.commit('setAllSecurities', users);
    },
    getAllSecurities: async () => {
        let value = store.getters.getSecurities;
        if (value) return value;
        value = await getValue(AllSecurities);
        return value;
    },

    setCurrentUser: async (currentUser) => {
        await setValue(CurrentUser, currentUser);
        store.commit('setCurrentUser', currentUser);
    },
    getCurrentUser: async () => {
        let value = store.getters.getCurrentUser;
        if (value) return value;
        value = await getValue(CurrentUser);
        return value;
    },

    /**
     * Persist the opaque deeplink sessionId minted at login.
     *
     * <p>Stored shape:
     * <pre>{ sessionId, sessionExpiresAt, storeDB, username, userId, issuedAt }</pre>
     *
     * <p>No password is stored - the sessionId is the only artifact that
     * crosses the deeplink boundary.
     *
     * @param payload the session payload from {@code POST /posAuth/{DB}/login}
     * @param storeDb the store database the session was minted for
     */
    setCurrentSession: async (payload, storeDb) => {
        if (!payload || !payload.sessionId) {
            return;
        }
        const value = {
            sessionId: payload.sessionId,
            sessionExpiresAt: payload.sessionExpiresAt,
            storeDB: storeDb,
            username: payload.username,
            userId: payload.empId,
            issuedAt: Date.now(),
        };
        await setValue(CURRENT_SESSION, value);
    },

    /**
     * Read the persisted deeplink session. Returns {@code null} if no
     * session has been minted yet, the storeDB doesn't match the
     * currently-active store, or the session has expired.
     *
     * @param expectedStoreDb optional; when supplied, returns null if
     *                        the persisted session was minted for a
     *                        different store
     * @return the session payload or null
     */
    getCurrentSession: async (expectedStoreDb) => {
        let value = await getValue(CURRENT_SESSION);
        if (!value || !value.sessionId) {
            return null;
        }
        if (expectedStoreDb && value.storeDB && value.storeDB !== expectedStoreDb) {
            return null;
        }
        if (value.sessionExpiresAt) {
            const expires = new Date(value.sessionExpiresAt).getTime();
            if (!Number.isNaN(expires) && expires < Date.now()) {
                return null;
            }
        }
        return value;
    },

    /**
     * Wipe the persisted deeplink session. Called on logout, on store
     * switch, and when the receiving app reports a 404.
     */
    clearCurrentSession: async () => {
        await setValue(CURRENT_SESSION, null);
    },
    stockByPLU: async (stock) => {
        await setValue(STOCK, stock);
    },
    getAllStock: async () => {
        let stock = await getValue(STOCK)
        let images = await Storage.getAllStockImages();
        stock = stock.map((item) => {
            let image = images.find(stockItem => stockItem.PLU === item.PLU);
            if (image) {
                item.Image = image.Image
            }
            return item;
        });
        return stock;
    },
    getAllStockImages: async () => {
        const img = await getValue(STOCK_IMAGES);
        if (!img) return [];
        return img;
    },
    getByPlu: async (plu) => {
        let stock = await getValue(STOCK) ?? [];
        let item = stock.find(item => item.PLU === plu)
        let images = await Storage.getAllStockImages();
        let image = images.find(stockItem => stockItem.PLU === item.PLU);
        if (image) {
            item.Image = image.Image
        }
        return item;
    },
    getFastLine: async () => {
        let stock = await getValue(STOCK) ?? [];
        let images = await Storage.getAllStockImages();
        stock = stock.map((item) => {
            let image = images.find(stockItem => stockItem.PLU === item.PLU);
            if (image) {
                item.Image = image.Image
            }
            return item;
        });
        return stock.filter(sc => sc.FastLine === 1);
    },
    searchStockDesc: async (query) => {
        if (notEmpty(query.category)) {
            return (await Storage.searchStock(query))
                .filter(s => s.Description.toLowerCase().includes(query.description.toLowerCase())
                    || s.Description2?.toLowerCase().includes(query.description.toLowerCase())
                    || s.PLU.toLowerCase().includes(query.description.toLowerCase())
                    || s.barcode.toLowerCase().includes(query.description.toLowerCase())
                ).sort((a, b) => {
                    return a.Description < b.Description;
                })
        }
        return (await Storage.getAllStock())
            .filter(s => s.Description.toLowerCase().includes(query.description.toLowerCase())
                || s.Description2?.toLowerCase().includes(query.description.toLowerCase())
                || s.PLU.toLowerCase().includes(query.description.toLowerCase())
                || s.barcode.toLowerCase().includes(query.description.toLowerCase())
            ).sort((a, b) => {
                return a.Description < b.Description;
            })
    },
    searchStock: async (query, limit = 50, offset = 0) => {
        let stock = await getValue(STOCK) ?? [];
        let result = [];

        if (query.category !== '' && query.subCategory !== '' && query.sub2Category !== '') {
            result = stock.filter(s => s.Department.toLowerCase() === query.category.toLowerCase())
                .filter(s2 => s2.SubDep.toLowerCase() === query.subCategory.toLowerCase())
                .filter(s3 => s3.Subdep2.toLowerCase() === query.sub2Category.toLowerCase());
        } else if (query.category !== '' && query.subCategory !== '' && query.sub2Category === '') {
            result = stock.filter(s => s.Department.toLowerCase() === query.category.toLowerCase())
                .filter(s2 => s2.SubDep.toLowerCase() === query.subCategory.toLowerCase());
        } else if (query.category !== '' && query.sub2Category === '' && query.subCategory === '') {
            result = stock.filter(s => s.Department.toLowerCase() === query.category.toLowerCase());
        }

        if (query.description) {
            result = result.filter(s => s.Description.toLowerCase().includes(query.description.toLowerCase())
                || s.PLU.toLowerCase().includes(query.description.toLowerCase())
                || s.barcode.toLowerCase().includes(query.description.toLowerCase())
            );
        }
        store.commit('setStockLength', result.length)
        result = result.slice(offset, offset + limit);

        if (result.length === 0) return;

        // Fetch images
        let images = await Storage.getAllStockImages();
        result = result.map((item) => {
            let image = images.find(stockItem => stockItem.PLU === item.PLU);
            if (image) {
                item.Image = image.Image;
            }
            return item;
        });

        // Sort results
        return result.sort((a, b) => {
            return a.Description < b.Description;
        });
    },
    editStockItem: async payload => {
        let stock = await getValue(STOCK);
        stock = stock.map(s => {
            if (s.PLU === payload.PLU) {
                return payload
            }
            return s;
        })
        await Storage.stockByPLU(stock);
    },
    setFloat: async (payload) => {
        await setValue(FLOAT, payload)
        store.commit('setUserFloat', payload);
    },
    getFloat: async () => {
        let value = store.getters.getFloats;
        if (value) return value;
        value = await getValue(FLOAT);
        return value;
    },
    setDeviceSettings: async (payload) => {
        await setValue(DEVICE_SETTINGS, payload)
        store.commit('setDeviceSettings', payload)
    },

    getDeviceSettings: async () => {
        let value = store.getters.getDeviceSettings;
        if (value) return value;
        value = await getValue(DEVICE_SETTINGS);
        return value;
    },
    getAllTables: async () => {
        let value = store.getters.getAllTables;
        if (value) return value;
        value = await getValue(SAVED_TABLES);
        return value;
    },

    setTables: async (payload) => {
        await setValue(SAVED_TABLES, payload)
    },

    createTable: async (payload) => {
        let value = await getValue(SAVED_TABLES);
        if (!value) {
            value = {
                value: JSON.stringify([]),
            }
        }
        let tables = JSON.parse(value);
        if (!tables) {
            tables = []
        }
        tables.push(payload)
        await setValue(SAVED_TABLES, JSON.stringify(tables));
    },

    editTable: async (payload) => {
        const value = await getValue(SAVED_TABLES);
        let tables = JSON.parse(value);
        tables = tables.map(table => {
            if (table.SaleNum === payload.SaleNum) {
                return payload
            }
            return table;
        })
        await setValue(SAVED_TABLES, JSON.stringify(tables));
    },

    deleteTable: async (salenum) => {
        const value = await getValue(SAVED_TABLES);
        let tables = JSON.parse(value);
        tables = tables.filter(table => table.SaleNum !== salenum);
        await setValue(SAVED_TABLES, JSON.stringify(tables));
    },

    clear: async () => {
        await clear();
    },
    clearAll: async () => {
        try {
            await Storage.clearAll();
        } catch (error) {
            console.error('Failed to clear storage:', error);
        }
    },


    setStockSettings: async (stock) => {
        await setValue(STOCK_SETTING, stock);
        store.commit('setStockSetting', stock);
    },
    getStockSetting: async (plu) => {
        let settings = store.getters.getStockSetting;
        if (settings) return settings.find(set => set.plu === plu);
        settings = await getValue(STOCK_SETTING);
        return settings.find(set => set.plu === plu);
    },

    setPaymentMethods: async (data) => {
        await setValue(PAYMENT_METHODS, data);
        store.commit('setPaymentMethods', data);
    },
    getPaymentMethods: async () => {
        let value = store.getters.getPaymentMethods;
        if (value) return value;
        value = await getValue(PAYMENT_METHODS);
        return value;
    },

    setKazangSession: async (data) => {
        await setValue(KAZANGSESSION, data);
        store.commit('setKazangSession', data);
    },
    getKazangSession: async () => {
        let value = store.getters.getKazangSessionID;
        if (value) return value;
        value = await getValue(KAZANGSESSION);
        return value;
    },

    setKazangProductList: async (data) => {
        await setValue(KAZANGPRODUCTS, data);
        store.commit('setKazangProductList', data);
    },
    getKazangProductList: async () => {
        let value = store.getters.getKazangProductList;
        if (value) return value;
        value = await getValue(KAZANGPRODUCTS);
        return value;
    },

    setBaseCurrency: async (data) => {
        await setValue(BASE_CURRENCY, data)
        store.commit('setBaseCurrency', data);
    },
    getBaseCurrency: async () => {
        let value = store.getters.getBaseCurrency;
        if (value) return value;
        value = await getValue(BASE_CURRENCY);
        return value;
    },

    setDiscountSpecials: async (payload) => {
        setValue(DISCOUNT_SPECIALS, payload)
        store.commit('setDiscountSpecials', payload);
    },
    getDiscountSpecials: async () => {
        let value = store.getters.getDiscountSpecials;
        if (value) return value;
        value = await getValue(DISCOUNT_SPECIALS);
        return value;
    },

    setMultiSpecials: async (payload) => {
        setValue(MULTI_SPECIALS, payload)
        store.commit('setMultiSpecials', payload);
    },
    getMultiSpecials: async () => {
        let value = store.getters.getMultiSpecials;
        if (value) return value;
        value = await getValue(MULTI_SPECIALS);
        return value;
    },

    setComboSpecials: async (payload) => {
        setValue(COMBO_SPECIALS, payload)
        store.commit('setComboSpecials', payload);
    },
    getComboSpecials: async () => {
        let value = store.getters.getComboSpecials;
        if (value) return value;
        value = await getValue(COMBO_SPECIALS);
        return value;
    },

    setTaxLabels: async (payload) => {
        setValue(TAX_LABELS, payload)
        store.commit('setTaxLabels', payload);
    },
    getAllTaxLabels: async () => {
        let value = store.getters.getTaxLabels;
        if (value) return value;
        value = await getValue(TAX_LABELS);
        return value;
    },

    setSystemSettings: async (payload) => {
        setValue(SYSTEM_SETTINGS, payload)
        store.commit('setSystemSettings', payload);
    },
    getSystemSettings: async () => {
        let value = store.getters.getSystemSettings;
        if (value) return value;
        value = await getValue(SYSTEM_SETTINGS);
        return value;
    },

    setHasLoyalty: async (payload) => {
        setValue(HAS_LOYALTY, payload)
        store.commit('setHasLoyalty', payload);
    },
    getHasLoyalty: async () => {
        let value = store.getters.getHasLoyalty;
        if (value) return value;
        value = await getValue(HAS_LOYALTY);
        return value;
    },

    setZedMobileStatus: async (payload) => {
        setValue(ZED_MOBILE_STATUS, payload)
        store.commit('setZedMobileStatus', payload);
    },
    getZedMobileStatus: async () => {
        let value = store.getters.getZedMobileStatus;
        if (value) return value;
        value = await getValue(ZED_MOBILE_STATUS);
        return value;
    },

    setStoreCurrencies: async (payload) => {
        setValue(STORE_CURRENCIES, payload)
        store.commit('setStoreCurrencies', payload);
    },
    getStoreCurrencies: async () => {
        let value = store.getters.getStoreCurrencies;
        if (value) return value;
        value = await getValue(STORE_CURRENCIES);
        return value;
    },

    waitForHydrate: async () => {
        if (!store.getters.isHydrated) {
            await new Promise((resolve) => {
                const interval = setInterval(() => {
                    if (store.getters.isHydrated) {
                        clearInterval(interval);
                        resolve();
                    }
                }, 50);
            });
        }
    },

    setSupervisor: async (supervisor) => {
        await setValue(SUPERVISOR, supervisor);
        store.commit('setSupervisor', supervisor);
    },

    getSupervisor: async () => {
        let value = store.getters.getSupervisor;
        if (value) return value;
        value = await getValue(SUPERVISOR);
        return value;
    },
}
